import { useState, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { COMPANY } from "../../lib/constants";
import { api, errMsg } from "../../lib/api";
import { fileToDataUrl } from "../../lib/upload";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Pencil, ImagePlus, Loader2, AlertTriangle } from "lucide-react";

export default function OpsSettings() {
  const { user, setUser } = useAuth();
  const { theme, toggle } = useTheme();
  const [editOpen, setEditOpen] = useState(false);
  const [profile, setProfile] = useState({ name: "", phone: "", picture: null, new_password: "" });
  const [saving, setSaving] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeText, setPurgeText] = useState("");
  const [purging, setPurging] = useState(false);
  const fileRef = useRef(null);

  const runPurge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge", { scope: "demo", confirm: purgeText });
      const total = Object.values(data.counts || {}).reduce((s, n) => s + n, 0);
      toast.success(`Wiped ${total} records — starting fresh`);
      setPurgeOpen(false); setPurgeText("");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) { toast.error(errMsg(e)); }
    setPurging(false);
  };

  const openEdit = () => {
    setProfile({ name: user?.name || "", phone: user?.phone || "", picture: user?.picture || null, new_password: "" });
    setEditOpen(true);
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setProfile((p) => ({ ...p, picture: null }));
      const dataUrl = await fileToDataUrl(file, 500);
      setProfile((p) => ({ ...p, picture: dataUrl }));
    } catch { toast.error("Could not read image"); }
    e.target.value = "";
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = { name: profile.name, phone: profile.phone };
      if (profile.picture !== null) payload.picture = profile.picture;
      if (profile.new_password) payload.new_password = profile.new_password;
      const { data } = await api.patch("/auth/profile", payload);
      setUser(data);
      toast.success("Profile updated");
      setEditOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  return (
    <div className="max-w-2xl" data-testid="ops-settings-page">
      <div className="mb-6">
        <p className="overline-cc">Configuration</p>
        <h1 className="page-title mt-1.5">Settings</h1>
      </div>
      <div className="space-y-5">
        <div className="rounded-xl border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold">Account</h2>
            <Button data-testid="settings-edit-profile-button" variant="outline" size="sm" className="rounded-full" onClick={openEdit}>
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit Profile
            </Button>
          </div>
          <div className="flex items-center gap-4 mb-5">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full object-cover border" data-testid="settings-avatar" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[var(--cc-accent)]/15 text-[var(--cc-accent)] flex items-center justify-center font-heading font-bold text-xl" data-testid="settings-avatar">
                {user?.name?.[0]}
              </div>
            )}
            <div>
              <p className="font-heading font-semibold" data-testid="settings-profile-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span data-testid="settings-profile-phone">{user?.phone || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Role</span><span className="font-mono-cc text-[var(--cc-accent)] uppercase text-xs tracking-wider">Administrator</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Auth provider</span><span className="capitalize">{user?.auth_provider}</span></div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-6">
          <h2 className="font-heading font-semibold mb-4">Appearance</h2>
          <div className="flex items-center justify-between">
            <div>
              <Label>Dark mode — Executive Operations Center</Label>
              <p className="text-xs text-muted-foreground mt-1">Matte black, graphite, cyan edge lighting.</p>
            </div>
            <Switch data-testid="settings-theme-switch" checked={theme === "dark"} onCheckedChange={toggle} />
          </div>
        </div>
        <div className="rounded-xl border bg-card p-6">
          <h2 className="font-heading font-semibold mb-4">Company Profile</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Company</span><span className="font-semibold">{COMPANY.name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Contact</span><span>{COMPANY.email}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Coverage</span><span className="text-right">{COMPANY.areas}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Insurance</span><span>{COMPANY.insured}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax rate</span><span className="font-mono-cc">8.25%</span></div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-6">
          <h2 className="font-heading font-semibold mb-2">Payments</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">Stripe checkout is active in test mode. Customers can pay open invoices from their portal; payments reconcile automatically via webhook and status polling.</p>
          <Button variant="outline" size="sm" className="rounded-full mt-4" onClick={() => window.open("https://dashboard.stripe.com", "_blank")} data-testid="settings-stripe-button">Open Stripe Dashboard</Button>
        </div>
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6">
          <h2 className="font-heading font-semibold mb-2 flex items-center gap-2 text-red-500">
            <AlertTriangle className="w-4 h-4" /> Danger Zone
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Wipe every mission, quote request, lead, invoice, earning, activity log, notification, review, and support chat.
            Users, employees, vehicles, and pricing settings stay intact. Use this to clear test data and start fresh.
          </p>
          <Button data-testid="settings-purge-button" variant="outline" size="sm"
            className="rounded-full mt-4 border-red-500/50 text-red-500 hover:bg-red-500/10"
            onClick={() => setPurgeOpen(true)}>
            Purge Demo Data
          </Button>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="profile-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit Profile</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {profile.picture ? (
                <img src={profile.picture} alt="preview" className="w-16 h-16 rounded-full object-cover border" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center text-muted-foreground border">
                  <ImagePlus className="w-5 h-5" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-photo-input" />
              <Button data-testid="profile-photo-button" type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()}>
                <ImagePlus className="w-3.5 h-3.5 mr-1.5" /> {profile.picture ? "Change Photo" : "Add Photo"}
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="profile-name-input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input data-testid="profile-phone-input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>New password (leave blank to keep current)</Label>
              <Input data-testid="profile-password-input" type="password" value={profile.new_password} onChange={(e) => setProfile({ ...profile, new_password: e.target.value })} placeholder="Min 6 characters" />
            </div>
            <Button data-testid="profile-save-button" onClick={saveProfile} disabled={saving || !profile.name.trim()}
              className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Profile"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={purgeOpen} onOpenChange={(o) => { if (!o) { setPurgeOpen(false); setPurgeText(""); } }}>
        <DialogContent data-testid="purge-confirm-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" /> Purge Demo Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              This wipes all missions, quotes, leads, invoices, earnings, activities, notifications, reviews and support chats.
              This cannot be undone. Type <span className="font-mono-cc font-bold text-foreground">PURGE</span> below to confirm.
            </p>
            <Input data-testid="purge-confirm-input" autoFocus value={purgeText}
              onChange={(e) => setPurgeText(e.target.value)} placeholder="Type PURGE to confirm" />
            <Button data-testid="purge-confirm-button" onClick={runPurge}
              disabled={purgeText !== "PURGE" || purging}
              className="w-full rounded-full bg-red-500 text-white hover:bg-red-600 font-semibold disabled:opacity-50">
              {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : "Wipe Everything"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
